import { useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ImageOff, ImageUp, Trash2 } from 'lucide-react';
import { getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { Button, toast } from '@blimo/ui';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

/**
 * Property image (G-04): preview, upload/replace and remove. Saves immediately (own endpoint), independent of the form.
 * The server validates type/size (JPEG/PNG/WebP/GIF, max. 10 MB) and the permission (`properties.update`).
 * @param {{ property: { id: number, name?: string, has_image?: boolean, image_version?: number|null } }} props
 */
export function PropertyImageField({ property }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const inputRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));

  /** @param {any} updated */
  const apply = (updated) => {
    queryClient.setQueryData(keys.admin.detail('properties', property.id), (previous) => ({ ...previous, ...updated }));
    queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'properties', 'list'] });
  };

  /** @param {File|undefined} file */
  async function upload(file) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      apply(await api.admin.properties.uploadImage(property.id, file));
      toast.success('Bild gespeichert');
    } catch (e) {
      setError(getErrorMessage(e, 'Das Bild konnte nicht gespeichert werden.'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.admin.properties.removeImage(property.id);
      apply({ has_image: false, image_version: null });
      toast.success('Bild entfernt');
    } catch (e) {
      setError(getErrorMessage(e, 'Das Bild konnte nicht entfernt werden.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <fieldset className="flex flex-col gap-2" aria-describedby={`${id}-hint`}>
      <legend className="text-sm font-medium">Bild der Liegenschaft</legend>
      <div className="flex items-start gap-3">
        <div className="flex aspect-[4/3] w-32 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
          {property.has_image ? (
            <img
              src={api.admin.properties.imageUrl(property.id, property.image_version)}
              alt={`Bild ${property.name ?? ''}`.trim()}
              className="size-full object-cover"
            />
          ) : (
            <ImageOff className="size-6 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            id={`${id}-file`}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => upload(e.target.files?.[0])}
            aria-label="Bilddatei auswählen"
          />
          <Button type="button" size="sm" variant="outline" loading={busy} onClick={() => inputRef.current?.click()}>
            <ImageUp aria-hidden="true" /> {property.has_image ? 'Bild ersetzen' : 'Bild hochladen'}
          </Button>
          {property.has_image && (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove}>
              <Trash2 aria-hidden="true" /> Bild entfernen
            </Button>
          )}
        </div>
      </div>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        JPEG, PNG, WebP oder GIF, max. 10 MB. Wird sofort gespeichert und im Kundenportal angezeigt.
      </p>
      {error && (
        <p role="alert" className="text-xs font-medium text-destructive">
          {error}
        </p>
      )}
    </fieldset>
  );
}
